// -----------------------------------------------------------------------------
// src/components/FavoritesSection.jsx (dashboards)
// -----------------------------------------------------------------------------
import React from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { publicGet } from "../lib/apiPublic";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import MiniArtistCard from "./MiniArtistCard";
import MiniHostCard from "./MiniHostCard";
import MiniArtworkCard from "./MiniArtworkCard";
import ArtworkCard from "./ArtworkCard";

export function FavoritesSection({
  type = "artworks",
  maxWidth = 1200, // same max width as page/tab bar
}) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Modal state for artworks
  const [selectedArtworkId, setSelectedArtworkId] = React.useState(null);
  const [selectedArtworkFull, setSelectedArtworkFull] = React.useState(null);
  const [artworkLoading, setArtworkLoading] = React.useState(false);

  const navigate = useNavigate();
  const trackRef = React.useRef(null);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.getJson(`/engagement/me/favorites/${type}`);
        if (!cancel) setItems(res?.results || []);
      } catch (e) {
        console.warn("favorites load failed", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [type]);

  // Lazy-fetch full artwork when an ID is chosen
  React.useEffect(() => {
    if (!selectedArtworkId) return;
    let cancel = false;
    setArtworkLoading(true);
    setSelectedArtworkFull(null);
    (async () => {
      try {
        const { data } = await publicGet(`/artworks/${selectedArtworkId}/`, { params: { view: "card" } });
        if (!cancel) setSelectedArtworkFull(data);
      } catch (e) {
        console.warn("Failed to fetch artwork detail", e);
      } finally {
        if (!cancel) setArtworkLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedArtworkId]);

  const title =
    type === "artists"
      ? "Artists"
      : type === "hosts"
      ? "Hosts"
      : "Artworks";

  const cardWidth = type === "artists" ? 280 : 240;

  const scrollByPage = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const page = Math.max(cardWidth + 16, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * page, behavior: "smooth" });
  };

  const closeModal = () => {
    setSelectedArtworkId(null);
    setSelectedArtworkFull(null);
    setArtworkLoading(false);
  };

  return (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(14px)',
        borderRadius: 16,
        boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
        padding: 16,
        position: "relative",
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "#111827",
          marginBottom: 12,
        }}
      >
        {title}
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div>
      ) : !items.length ? (
        <div style={{ fontSize: 13, color: "#6b7280" }}>No favorites yet.</div>
      ) : (
        // Responsive wrapper: full width on mobile, capped maxWidth otherwise
        <Box
          sx={{
            width: "100%",
            maxWidth,
            mx: "auto",
            position: "relative",
          }}
        >
          {/* Left / Right buttons */}
          {!isSm && (
            <>
              <IconButton
                aria-label="Previous"
                onClick={() => scrollByPage(-1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: -8,
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.9)",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                }}
                size="small"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              <IconButton
                aria-label="Next"
                onClick={() => scrollByPage(1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: -8,
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.9)",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                }}
                size="small"
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </>
          )}

          {/* Horizontal scroll track */}
          <Box
            ref={trackRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollBehavior: "smooth",
              WebkitOverflowScrolling: "touch",
              px: 1,
              py: 0.5,
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d1d5db",
                borderRadius: 8,
              },
              "& > *": {
                scrollSnapAlign: "start",
                flex: "0 0 auto",
              },
            }}
          >
            {items.map((it) => {
              const id = it?.id ?? it?.object_id ?? it?.pk;
              if (!id) return null;

              if (type === "artists") {
                return (
                  <Box key={`artist-${id}`}>
                    <MiniArtistCard
                      id={id}
                      onViewProfile={() => navigate(`/artists/${id}`)}
                    />
                  </Box>
                );
              }

              if (type === "hosts") {
                return (
                  <Box key={`host-${id}`}>
                    <MiniHostCard
                      id={id}
                      onViewProfile={() => navigate(`/hosts/${id}`)}
                    />
                  </Box>
                );
              }

              // ARTWORKS
              return (
                <Box key={`art-${id}`}>
                  <MiniArtworkCard
                    id={id}
                    onClick={() => setSelectedArtworkId(id)}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Artwork modal with lazy-fetched detail */}
      <Dialog open={!!selectedArtworkId} onClose={closeModal} maxWidth="md" fullWidth>
        <Box sx={{ p: 2, bgcolor: "#f9fafb" }}>
          {artworkLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : selectedArtworkFull ? (
            <ArtworkCard artwork={selectedArtworkFull} showActions={false} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Failed to load artwork details.
            </Typography>
          )}
        </Box>
      </Dialog>
    </div>
  );
}
